import React, { useState, useEffect } from 'react';
import { FormError } from 'components';
import { connect } from 'react-redux';
import { CFCProps } from 'types';
import { RootState } from 'redux/root.reducer';
import { TextInput, Button, InlineLoading } from 'carbon-components-react';
import { ReactComponent as HighRiskUserIcon } from 'assets/icons/high--risk--users.svg';

const OrganisationProfileForm: React.FC<Props | any> = (props: any) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isDataUpdated, setIsDataUpdated] = useState<boolean>(false);
  const {
    app,
    memberCache,
    values,
    touched: fTouched,
    errors: fErrors,
    handleChange,
    handleBlur,
    handleSubmit
  } = props;

  // eslint-disable-next-line react/display-name
  const inputError = ((errors: any, touched: any) => (
    key:
      | 'contactEmail'
      | 'contactName'
      | 'contactPhoneNumber'
      | 'contactEmergencyPhone'
      | 'contactUrl'
  ) => errors[key] && touched[key] && <FormError>{errors[key]}</FormError>)(
    fErrors,
    fTouched
  );

  useEffect(() => {
    setIsLoading(app?.loading);
    setIsSubmitting(memberCache?.submitting);
    setIsDataUpdated(memberCache?.orgProfileUpdated);
  }, [app, memberCache]);

  useEffect(() => {
    if (isDataUpdated) {
      setTimeout(() => {
        setIsDataUpdated(false);
      }, 1000);
    }
  }, [isDataUpdated]);

  const isSaveEnabled =
    values.contactEmail &&
    values.contactName &&
    values.contactPhoneNumber &&
    values.contactEmergencyPhone &&
    !fErrors.contactEmail &&
    !fErrors.contactName &&
    !fErrors.contactPhoneNumber &&
    !fErrors.contactEmergencyPhone &&
    !fErrors.contactUrl;

  return (
    <section className="org-profile-form">
      <form onSubmit={handleSubmit}>
        <section className="org-profile-form__section">
          <h6 className="org-profile-form__header">
            General information is shared with enquirers for everyday operations
          </h6>
          <section className="form-section">
            <section className="form-item">
              <TextInput
                id="contactName"
                name="contactName"
                labelText="Contact name"
                placeholder="Please enter entity, person or team name"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.contactName}
                data-testid="contactName"
                helperText="Can be a person or team name"
                invalid={fErrors.contactName}
              />
              {inputError('contactName')}
            </section>
            <section className="form-item">
              <TextInput
                id="contactEmail"
                name="contactEmail"
                labelText="Email address"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.contactEmail}
                data-testid="contactEmail"
                placeholder="Please enter email address"
                helperText="Reply-to address for all communications between you and enquirers"
                invalid={fErrors.contactEmail}
              />
              {inputError('contactEmail')}
            </section>
            <section className="form-item">
              <TextInput
                id="contactPhoneNumber"
                name="contactPhoneNumber"
                labelText="Phone (general)"
                onChange={handleChange}
                onBlur={handleBlur}
                type="tel"
                value={values.contactPhoneNumber}
                data-testid="contactPhoneNumber"
                placeholder="Please enter phone number"
                invalid={fErrors.contactPhoneNumber}
              />
              {inputError('contactPhoneNumber')}
            </section>
            <section className="form-item">
              <TextInput
                id="contactUrl"
                name="contactUrl"
                labelText="Web URL"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.contactUrl}
                data-testid="contactUrl"
                placeholder="Please enter web URL"
                helperText="Include a link to a web page for handling enquiries"
                invalid={fErrors.contactUrl}
              />
              {inputError('contactUrl')}
            </section>
          </section>
        </section>
        <hr className="section--divider" />
        <section className="org-profile-form__section">
          <h6 className="org-profile-form__header org-profile-form__header--red">
            Emergency information is shared when enquirers want to report damage
            or other emergencies
          </h6>
          <section className="form-section form-section--flex">
            <section className="form-item">
              <TextInput
                id="contactEmergencyPhone"
                name="contactEmergencyPhone"
                labelText="Emergency phone"
                onChange={handleChange}
                onBlur={handleBlur}
                type="tel"
                value={values.contactEmergencyPhone}
                data-testid="contactEmergencyPhone"
                placeholder="Please enter emergency phone"
                invalid={fErrors.contactEmergencyPhone}
              />
              {inputError('contactEmergencyPhone')}
            </section>
            <HighRiskUserIcon className="emergency-icon" />
          </section>
          <section className="form-cta">
            {!isSubmitting && !isDataUpdated ? (
              <Button
                size="field"
                className="org-profile-submit"
                type="submit"
                kind={!fErrors ? 'danger' : 'primary'}
                data-testid="submit"
                disabled={isLoading || isSubmitting || !isSaveEnabled}
              >
                Save
              </Button>
            ) : (
              <div className="org-profile-submit org-profile-submit--loader">
                <InlineLoading
                  description={
                    !isSubmitting && isDataUpdated ? 'Saved' : 'Saving ...'
                  }
                  status={
                    !isSubmitting && isDataUpdated ? 'finished' : 'active'
                  }
                  aria-live={'assertive'}
                />
              </div>
            )}
          </section>
        </section>
      </form>
    </section>
  );
};

const mapStateToProps = ({ app, memberCache }: RootState) => ({
  app,
  memberCache
});

const connector = connect(mapStateToProps);

type Props = CFCProps<typeof connector>;

OrganisationProfileForm.displayName = 'OrganisationProfileForm';

export default connector(OrganisationProfileForm);
